import { QuestAtlas } from "./components/QuestAtlas";
import { useState } from "react";
import { format, parseISO } from "date-fns";
import { DayPanel } from "./components/DayPanel";
import { MapView } from "./components/MapView";
import { TodoList } from "./components/TodoList";
import { StatusBadge } from "./components/StatusBadge";
import { Icon } from "./components/Icon";
import { IdeasPanel } from "./components/IdeasPanel";
import { findPinForScheduleItem } from "./components/PlacePhoto";
import { PlaceDetail } from "./components/PlaceDetail";
import { useTripData } from "./hooks/useTripData";
import { PasswordGate } from "./components/PasswordGate";
import {
  clearStoredVoter,
  readStoredVoter,
  writeStoredVoter,
} from "./lib/voterSession";
import { PlacePhotosProvider, useResolvedPinPhoto } from "./hooks/usePlacePhotos";
import { pinMayHavePhoto } from "./lib/googlePlaces";
import type { DayPlan, MapPin, TravelerId } from "./types";
import "./styles/app.css";
import "./styles/journey.css";

const regions = ["All days", "Santiago", "Atacama", "Patagonia", "Travel days"];
const tabs = [
  { name: "Explore", icon: "map" },
  { name: "Itinerary", icon: "calendar" },
  { name: "Ideas", icon: "list" },
  { name: "Stays", icon: "bed" },
  { name: "Map", icon: "map" },
  { name: "Checklist", icon: "check" },
];
function regionOf(day: DayPlan) {
  if (day.date >= "2026-11-20" && day.date <= "2026-11-23") return "Atacama";
  if (day.date >= "2026-11-24" && day.date <= "2026-11-30") return "Patagonia";
  if (day.date === "2026-11-14" || day.date >= "2026-12-02")
    return "Travel days";
  return "Santiago";
}


function DayThumb({ pin }: { pin?: MapPin }) {
  const resolved = useResolvedPinPhoto(pin);
  const url = resolved.url ?? pin?.imageUrl;
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={(e) => {
        if (resolved.source === "places") resolved.onError();
        else e.currentTarget.style.display = "none";
      }}
    />
  );
}

export default function App() {
  const [voterId, setVoterId] = useState<TravelerId | null>(() => readStoredVoter());
  const store = useTripData();
  const { data, setPinPlaceId } = store;
  const [selectedDate, setSelectedDate] = useState(
    data.days[0]?.date ?? data.meta.startDate,
  );
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(selectedDate);
  const [tab, setTab] = useState("Explore");
  const [region, setRegion] = useState("All days");
  const [notice, setNotice] = useState("");
  const [settings, setSettings] = useState(false);
  const safeDate = store.getDay(selectedDate)
    ? selectedDate
    : (data.days[0]?.date ?? data.meta.startDate);
  const remaining = data.days.reduce(
    (n, d) => n + d.todos.filter((t) => !t.done).length,
    0,
  );
  const total = data.days.reduce((n, d) => n + d.todos.length, 0);
  const confirmed = data.days
    .flatMap((d) => d.items)
    .filter((i) => i.status === "confirmed").length;
  const days = data.days.filter(
    (d) => region === "All days" || regionOf(d) === region,
  );
  const hero =
    data.pins.find((p) => p.id === "pin-paine-grande") ??
    data.pins.find((p) => p.imageUrl);
  const selectDate = (date: string) => {
    setSelectedDate(date);
    setExpanded(date);
  };
  const showDay = (date: string) => {
    selectDate(date);
    setRegion("All days");
    setTab("Itinerary");
  };
  const photoFor = (day: DayPlan) =>
    data.pins.find(
      (p) => pinMayHavePhoto(p) && p.dayIds?.includes(day.date),
    );

  if (!voterId) {
    return (
      <PasswordGate
        onUnlock={(id) => {
          writeStoredVoter(id);
          setVoterId(id);
        }}
      />
    );
  }

  return (
    <PlacePhotosProvider pins={data.pins} onPlaceIdResolved={setPinPlaceId}>
    <div className="journey-app">
      <a href="#journey-content" className="skip-link">
        Skip to trip plan
      </a>
      <header className="journey-topbar">
        <a
          href="#"
          className="journey-brand"
          aria-label="Chile trip home"
          onClick={() => {
            setTab("Explore");
            setRegion("All days");
          }}
        >
          <span className="logo">
            <Icon name="mountain" size={25} />
          </span>
          <span>
            chile<span className="brand-dot">.</span>
          </span>
        </a>
        <div className="header-caption">A little further from ordinary</div>
        <div className="header-actions">
          <span className="saved-label">
            <i /> Saved on this device
          </span>
          <button
            className="btn"
            onClick={() => {
              store.exportJson();
              setNotice(
                "Trip exported. Send the file to share your latest plans.",
              );
            }}
          >
            <Icon name="download" size={16} /> Export trip
          </button>
          <button
            type="button"
            className="logout-link"
            aria-label={
              voterId === "michael" ? "Mike, Sign out" : "Suzy, Sign out"
            }
            onClick={() => {
              clearStoredVoter();
              setVoterId(null);
            }}
          >
            {voterId === "michael" ? "Mike · Sign out" : "Suzy · Sign out"}
          </button>
          <div
            className="avatars"
            aria-label={data.meta.travelers
              .map((t) => t.shortName)
              .join(" and ")}
          >
            {data.meta.travelers.map((t) => (
              <span key={t.id} className={t.id === voterId ? "is-you" : undefined}>
                {t.shortName[0]}
              </span>
            ))}
          </div>
        </div>
      </header>
      <main className="journey-layout">
        <aside className="trip-overview">
          <div className="cover">
            <img
              src={hero?.imageUrl}
              alt={hero?.imageAlt ?? "Mountains in Torres del Paine"}
            />
            <span className="cover-label">
              <Icon name="pin" size={13} /> PATAGONIA, CHILE
            </span>
            <div className="cover-title">
              The long way
              <br />
              to somewhere good.
            </div>
            <span className="photo-credit">{hero?.imageCredit}</span>
          </div>
          <div className="overview-heading">
            <p className="eyebrow">OUR NEXT ADVENTURE</p>
            <h1>
              Chile &<br />
              Torres del Paine
            </h1>
            <p className="trip-dates">
              <Icon name="calendar" size={16} />{" "}
              {format(parseISO(data.meta.startDate), "d MMM")} –{" "}
              {format(parseISO(data.meta.endDate), "d MMM yyyy")}
            </p>
          </div>
          <div className="trip-facts">
            <div>
              <Icon name="clock" size={17} />
              <span>Duration</span>
              <strong>{data.days.length} days</strong>
            </div>
            <div>
              <Icon name="mountain" size={18} />
              <span>Travelers</span>
              <strong>{data.meta.travelers.length} adventurers</strong>
            </div>
          </div>
          <section className="overview-section">
            <h2>The journey</h2>
            <div className="route-stops">
              {["Santiago", "Atacama", "Patagonia"].map((r, i) => (
                <button
                  key={r}
                  onClick={() => {
                    setRegion(r);
                    setTab("Itinerary");
                  }}
                >
                  <span className={`route-dot dot-${i}`} />
                  <span>
                    <strong>{r}</strong>
                    <small>
                      {
                        [
                          "City days & a soft landing",
                          "Desert skies & salt flats",
                          "The W trek & wild places",
                        ][i]
                      }
                    </small>
                  </span>
                  <Icon name="arrow" size={16} />
                </button>
              ))}
            </div>
          </section>
          <section className="readiness">
            <div>
              <span className="eyebrow">GETTING READY</span>
              <span>
                {total - remaining}/{total}
              </span>
            </div>
            <h3>A great trip starts here.</h3>
            <progress
              aria-label="Trip preparation complete"
              value={total - remaining}
              max={total || 1}
            />
            <button onClick={() => setTab("Checklist")}>
              {remaining
                ? `${remaining} things left to do`
                : "All tasks complete"}
              <Icon name="arrow" size={17} />
            </button>
          </section>
          <button
            className="manage-trip"
            onClick={() => setSettings(!settings)}
            aria-expanded={settings}
          >
            Trip notes & backup <span>{settings ? "−" : "+"}</span>
          </button>
          {settings && (
            <section className="trip-settings">
              <h3>Trip notes</h3>
              {data.meta.notes.map((n) => (
                <p key={n}>
                  {n.split(/(https?:\/\/\S+)/).map((part, i) =>
                    part.startsWith("http") ? (
                      <a key={i} href={part} target="_blank" rel="noreferrer">
                        Open resource ↗
                      </a>
                    ) : (
                      part
                    ),
                  )}
                </p>
              ))}
              <p>
                Changes stay on this device. Export a backup or import one
                shared with you.
              </p>
              <label className="import-label">
                Import trip
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (
                      !confirm(
                        "Replace the trip on this device with the imported file? Export first to keep a backup.",
                      )
                    ) {
                      e.target.value = "";
                      return;
                    }
                    try {
                      await store.importJson(file);
                      setNotice("Trip imported successfully.");
                      setRegion("All days");
                      setExpanded(null);
                    } catch {
                      setNotice(
                        "That file could not be imported. Your trip has not changed.",
                      );
                    }
                    e.target.value = "";
                  }}
                />
              </label>
              <button
                className="reset-link"
                onClick={() => {
                  if (confirm("Reset all changes to the original itinerary?")) {
                    store.resetToSeed();
                    setNotice("Original itinerary restored.");
                  }
                }}
              >
                Restore original itinerary
              </button>
            </section>
          )}
          <p className="overview-footer">
            Made for {data.meta.travelers.map((t) => t.shortName).join(" & ")}
            <br />
            <span>One adventure. A thousand memories.</span>
          </p>
        </aside>
        <section id="journey-content" className="journey-content">
          <div className="content-heading">
            <div>
              <p className="eyebrow">
                {format(parseISO(data.meta.startDate), "MMMM")} —{" "}
                {format(parseISO(data.meta.endDate), "MMMM yyyy")}
              </p>
              <h2>Your next chapter.</h2>
              <p>
                From city mornings to mountain miles. All your plans, in one
                place.
              </p>
            </div>
            <span className="trip-tag">
              <span /> Upcoming trip
            </span>
          </div>
          <nav className="journey-tabs" aria-label="Trip views">
            {tabs.map((t) => (
              <button
                key={t.name}
                aria-current={tab === t.name ? "page" : undefined}
                className={tab === t.name ? "active" : ""}
                onClick={() => setTab(t.name)}
              >
                <Icon name={t.icon} size={17} />
                {t.name}
                {t.name === "Checklist" && (
                  <span className="tab-count">{remaining}</span>
                )}
              </button>
            ))}
          </nav>
          {notice && (
            <div className="notice" role="status">
              {notice}
              <button
                onClick={() => setNotice("")}
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          )}
          <div className="view-heading">
            <div>
              <h3>
                {tab === "Itinerary"
                  ? "A journey to remember"
                  : tab === "Ideas"
                    ? "What should we do?"
                    : tab === "Stays"
                      ? "Somewhere to settle in"
                      : tab === "Map"
                        ? "Find your way"
                        : "A little preparation, more adventure"}
              </h3>
              <p>
                {tab === "Itinerary"
                  ? `${data.days.length} days of discovery · ${confirmed} confirmed plans · mornings first`
                  : tab === "Ideas"
                    ? `${data.ideas.length} optional attractions · vote 👍/👎`
                    : tab === "Stays"
                      ? "Your overnight plans, with stay details close at hand."
                      : tab === "Map"
                        ? "Explore the places along your route."
                        : "Check things off as you go. Changes are saved on this device."}
              </p>
            </div>
            {tab === "Itinerary" && (
              <span className="view-label">
                <Icon name="list" size={16} /> Day by day
              </span>
            )}
          </div>
          {tab !== "Map" && tab !== "Ideas" && (
            <div className="region-filters" aria-label="Filter by destination">
              {regions.map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  aria-pressed={region === r}
                  className={region === r ? "selected" : ""}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
          {tab === "Explore" && <QuestAtlas store={store} date={safeDate} onSelectDate={selectDate} voterId={voterId} selectedItemId={selectedItemId} onSelectItem={setSelectedItemId} />}
          {tab === "Itinerary" && (
            <div className="day-cards">
              {days.map((day) => {
                const open = expanded === day.date;
                const index = data.days.indexOf(day);
                const photo = photoFor(day);
                const todos = day.todos.filter((t) => !t.done).length;
                return (
                  <article
                    key={day.date}
                    className={`journey-day ${open ? "is-open" : ""}`}
                  >
                    <button
                      className="day-toggle"
                      aria-expanded={open}
                      aria-controls={`day-${day.date}`}
                      onClick={() => {
                        selectDate(day.date);
                        setExpanded(open ? null : day.date);
                      }}
                    >
                      <div className="day-thumbnail">
                        {photo ? (
                          <DayThumb pin={photo} />
                        ) : (
                          <Icon
                            name={
                              regionOf(day) === "Travel days"
                                ? "plane"
                                : "mountain"
                            }
                            size={26}
                          />
                        )}
                      </div>
                      <div className="day-card-title">
                        <span>
                          DAY {String(index + 1).padStart(2, "0")}
                          <i />
                          {format(parseISO(day.date), "EEE, d MMM")}
                        </span>
                        <h4>{day.title}</h4>
                        <p>
                          <Icon name="pin" size={12} />
                          {day.location ?? regionOf(day)}
                        </p>
                      </div>
                      <span className="day-card-meta">
                        {todos > 0
                          ? `${todos} to-do${todos === 1 ? "" : "s"}`
                          : `${day.items.length} plan${day.items.length === 1 ? "" : "s"}`}
                      </span>
                      <span className="expand-circle">
                        <Icon name={open ? "minus" : "plus"} size={18} />
                      </span>
                    </button>
                    {open && (
                      <div id={`day-${day.date}`} className="expanded-day">
<DayPanel
                          compact
                          date={day.date}
                          store={store}
                          voterId={voterId}
                          onSelectDate={selectDate}
                          selectedItemId={selectedItemId}
                          onSelectItem={setSelectedItemId}
                        />
                        <button
                          className="day-map-link"
                          onClick={() => {
                            selectDate(day.date);
                            setTab("Map");
                          }}
                        >
                          <Icon name="map" size={16} /> Open full trip map
                          <Icon name="arrow" size={16} />
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
          {tab === "Ideas" && (
            <IdeasPanel
              store={store}
              voterId={voterId}
              selectedDate={safeDate}
              onSelectDate={(date) => {
                selectDate(date);
                setTab("Itinerary");
                setExpanded(date);
              }}
            />
          )}
          {tab === "Stays" && (
            <div className="stay-cards">
              {days.flatMap((day) =>
                day.items
                  .filter((i) => i.category === "lodging")
                  .map((item) => {
                    const pin = findPinForScheduleItem(
                      item,
                      data.pins.filter((p) => p.dayIds?.includes(day.date)),
                      data.pins,
                    );
                    return (
                      <article
                        className="stay-card"
                        key={`${day.date}-${item.id}`}
                      >
                        <div className="stay-icon">
                          <Icon name="bed" size={23} />
                        </div>
                        <div>
                          <p className="eyebrow">
                            {format(parseISO(day.date), "EEE, d MMM")}
                          </p>
                          <h4>{item.title}</h4>
                          <StatusBadge status={item.status} />
                          {pin && pinMayHavePhoto(pin) && <PlaceDetail pin={pin} compact />}
                          {item.detail && <p>{item.detail}</p>}
                          <button
                            className="text-action"
                            onClick={() => showDay(day.date)}
                          >
                            View day <Icon name="arrow" size={15} />
                          </button>
                        </div>
                      </article>
                    );
                  }),
              )}
              {!days.some((d) =>
                d.items.some((i) => i.category === "lodging"),
              ) && (
                <div className="empty-state">
                  <Icon name="bed" size={30} />
                  <h4>No stays planned here yet</h4>
                  <p>Choose another destination to see your overnight plans.</p>
                </div>
              )}
            </div>
          )}
          {tab === "Checklist" && (
            <div className="checklist-groups">
              {days.map((day) => (
                <section key={day.date}>
                  <button
                    className="checklist-day-title"
                    onClick={() => showDay(day.date)}
                  >
                    <span>
                      {format(parseISO(day.date), "d MMM")}{" "}
                      <strong>{day.title}</strong>
                    </span>
                    <Icon name="arrow" size={16} />
                  </button>
                  <TodoList
                    todos={day.todos}
                    onToggle={(id) => store.toggleTodo(day.date, id)}
                    onAdd={(text) => store.addTodo(day.date, text)}
                    onRemove={(id) => store.removeTodo(day.date, id)}
                    onEdit={(id, text) => store.editTodo(day.date, id, text)}
                  />
                </section>
              ))}
            </div>
          )}
          {tab === "Map" && (
            <>
              <label className="map-date-selector">
                Explore a day
                <select
                  value={safeDate}
                  onChange={(e) => selectDate(e.target.value)}
                >
                  {data.days.map((d) => (
                    <option key={d.date} value={d.date}>
                      {format(parseISO(d.date), "d MMM")} · {d.title}
                    </option>
                  ))}
                </select>
              </label>
              <MapView
                data={data}
                selectedDate={safeDate}
                selectedItemId={selectedItemId}
                onSelectDate={selectDate}
              />
            </>
          )}
          <footer className="content-footer">
            <Icon name="mountain" size={18} />
            <span>Leave room for the unexpected.</span>
            <span>CHILE · 2026</span>
          </footer>
        </section>
      </main>
    </div>
    </PlacePhotosProvider>
  );
}

